import React, { useState } from 'react';
import { Phone, Send, User, Mail, MessageSquare, Calendar } from 'lucide-react';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { whatsappNumber, formattedWhatsappNumber } from '../data/mock';

const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    date: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.phone || !formData.service) {
      toast.error(t('contact.form.required'));
      return;
    }

    // Create WhatsApp message
    const message = `${t('contact.form.name')}: ${formData.name}\n${t('contact.form.email')}: ${formData.email}\n${t('contact.form.phone')}: ${formData.phone}\n${t('contact.form.service')}: ${formData.service}\n${t('contact.form.date')}: ${formData.date}\n${t('contact.form.message')}: ${formData.message}`;
    
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    
    toast.success(t('contact.form.success'));
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      service: '',
      date: '',
      message: ''
    });
  };

  return (
    <section id="contatti" className="py-20 bg-gradient-to-b from-gray-950 via-gray-900 to-black scroll-mt-20">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block">
            <span className="text-amber-400 font-semibold text-sm uppercase tracking-wider mb-2 block">
              {t('contact.badge')}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t('contact.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-amber-600 mx-auto"></div>
          </div>
          <p className="text-gray-400 text-lg mt-6 max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-8">
          {/* Contact Form */}
          <Card className="bg-gradient-to-br from-gray-900 to-gray-950 border-gray-800 p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-white flex items-center">
                  <User className="w-4 h-4 mr-2 text-amber-400" />
                  {t('contact.form.name')} *
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Mario Rossi"
                  required
                  className="bg-gray-950 border-gray-700 text-white placeholder:text-gray-500 focus:border-amber-500"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white flex items-center">
                  <Mail className="w-4 h-4 mr-2 text-amber-400" />
                  {t('contact.form.email')}
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="mario.rossi@email.com"
                  className="bg-gray-950 border-gray-700 text-white placeholder:text-gray-500 focus:border-amber-500"
                />
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-white flex items-center">
                  <Phone className="w-4 h-4 mr-2 text-amber-400" />
                  {t('contact.form.phone')} *
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+39 333 1234567"
                  required
                  className="bg-gray-950 border-gray-700 text-white placeholder:text-gray-500 focus:border-amber-500"
                />
              </div>

              {/* Service */}
              <div className="space-y-2">
                <Label htmlFor="service" className="text-white flex items-center">
                  <MessageSquare className="w-4 h-4 mr-2 text-amber-400" />
                  {t('contact.form.service')} *
                </Label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-950 border border-gray-700 text-white rounded-md px-3 py-2 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                >
                  <option value="">{t('contact.form.servicePlaceholder')}</option>
                  <option value={t('contact.form.serviceOptions.airport')}>{t('contact.form.serviceOptions.airport')}</option>
                  <option value={t('contact.form.serviceOptions.business')}>{t('contact.form.serviceOptions.business')}</option>
                  <option value={t('contact.form.serviceOptions.ceremony')}>{t('contact.form.serviceOptions.ceremony')}</option>
                  <option value={t('contact.form.serviceOptions.tour')}>{t('contact.form.serviceOptions.tour')}</option>
                  <option value={t('contact.form.serviceOptions.other')}>{t('contact.form.serviceOptions.other')}</option>
                </select>
              </div>

              {/* Date */}
              <div className="space-y-2">
                <Label htmlFor="date" className="text-white flex items-center">
                  <Calendar className="w-4 h-4 mr-2 text-amber-400" />
                  {t('contact.form.date')}
                </Label>
                <Input
                  id="date"
                  name="date"
                  type="date"
                  value={formData.date}
                  onChange={handleChange}
                  className="bg-gray-950 border-gray-700 text-white focus:border-amber-500"
                />
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message" className="text-white">
                  {t('contact.form.message')}
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t('contact.form.messagePlaceholder')}
                  rows={4}
                  className="bg-gray-950 border-gray-700 text-white placeholder:text-gray-500 focus:border-amber-500 resize-none"
                />
              </div>

              {/* Submit Button */}
              <Button 
                type="submit"
                className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-bold py-6 rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-amber-500/50"
              >
                <Send className="w-5 h-5 mr-2" />
                {t('contact.form.submit')}
              </Button>
            </form>
          </Card>

          {/* Contact Info & CTA */}
          <div className="space-y-6">
            {/* WhatsApp Direct */}
            <Card className="bg-gradient-to-br from-green-900/20 to-green-950/20 border-green-700/30 p-8">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center">
                  <Phone className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-1">{t('contact.whatsapp.title')}</h3>
                  <p className="text-gray-400">{t('contact.whatsapp.subtitle')}</p>
                </div>
              </div>
              
              <Button 
                onClick={() => window.open(`https://wa.me/${whatsappNumber}`, '_blank')}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-6 rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-green-500/50"
              >
                <Phone className="w-5 h-5 mr-2" />
                {t('contact.whatsapp.cta')}
              </Button>
              
              <p className="text-center text-white font-semibold text-xl mt-4">
                {formattedWhatsappNumber}
              </p>
            </Card>

            {/* Why Choose Us */}
            <Card className="bg-gradient-to-br from-gray-900 to-gray-950 border-gray-800 p-8">
              <h3 className="text-xl font-bold text-white mb-6">{t('contact.why.title')}</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span className="text-gray-300">{t('contact.why.reason1')}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span className="text-gray-300">{t('contact.why.reason2')}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span className="text-gray-300">{t('contact.why.reason3')}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span className="text-gray-300">{t('contact.why.reason4')}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span className="text-gray-300">{t('contact.why.reason5')}</span>
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
